import React, { createContext, useState, useMemo, ReactNode } from "react";
import toast, { Toaster, ToastPosition } from "react-hot-toast";

export type ToastType = "success" | "error" | "loading";

export interface ToastOptions {
  duration?: number;
  position?: ToastPosition;
}

export const GlobalContext = createContext<{
  menuIsOpen: boolean;
  menuSectionExpanded: string | null;
  setMenuSectionExpanded: (value: string | null) => void;
  setMenuIsOpen: (value: boolean) => void;
  showToast: (message: string, type?: ToastType, options?: ToastOptions) => void;
}>(null);

export const GlobalContextProvider = ({ children }: { children: ReactNode }) => {
  const [menuIsOpen, setMenuIsOpen] = useState<boolean>(true);
  const [menuSectionExpanded, setMenuSectionExpanded] = useState<string | null>(null);

  const showToast = (message: string, type: ToastType = "success", options: ToastOptions = {}) => {
    const defaultOptions = {
      duration: 4000,
      position: "top-right" as ToastPosition,
      ...options,
    };

    switch (type) {
      case "success":
        toast.success(message, defaultOptions);
        break;
      case "error":
        toast.error(message, defaultOptions);
        break;
      case "loading":
        toast.loading(message, defaultOptions);
        break;
      default:
        toast(message, defaultOptions);
    }
  };

  const value = useMemo(() => {
    return {
      menuIsOpen,
      menuSectionExpanded,
      setMenuIsOpen,
      setMenuSectionExpanded,
      showToast,
    };
  }, [menuIsOpen, menuSectionExpanded]);

  return (
    <GlobalContext.Provider value={value}>
      <Toaster
        position="top-right"
        containerStyle={{
          filter: "drop-shadow(0 4px 12px rgba(0, 0, 0, 0.1))"
        }}
        toastOptions={{
          style: {
            fontFamily: 'Helvetica, sans-serif',
            fontSize: '14px',
            fontWeight: 500
          }
        }}
      />
      {children}
    </GlobalContext.Provider>
  );
};

export const useGlobalContext = () => React.useContext(GlobalContext);
